import type { CreateParameterFormData, Parameter } from "../parameter/parameter";


/**
 * The types of insurance products / definitions that exist in the database.
 */
export type InsuranceTypes =
    | "wc" // Lovpålagt Yrkesskade
    | "extended-wc-accident" // Utvidet Yrkesskade (Ulykke)
    | "extended-wc-illness" // Utvidet Yrkesskade (Sykdom)
    | "extended-wc-death" // Utvidet Yrkesskade (Død)
    | "leisure-accident-invalidity" // Fritidsulykke (invaliditet)
    | "leisure-accident-disability" // Fritidsulykke (arbeidsuførhet)
    | "leisure-accident-death" // Fritidsulykke (død)
    | "trygghetsforsikring" // Trygghetsforsikring (invaliditet + død)
    | "collective-accident-invalidity" // Kollektiv ulykke (invaliditet)
    | "collective-accident-death" // Kollektiv ulykke (død)
    | "other-illness-invalidity" // Annen sykdom (invaliditet)
    | "other-illness-disability" // Annen sykdom (arbeidsuførhet)						
    | "group-life-coordination" // Gruppeliv - Samordning
    | "group-life-base-coverage" // Gruppeliv - HF
    | "group-life-child-supplement" // Gruppeliv - Barnetillegg
    | "group-life-annual-salary" // Gruppeliv - Årslønn
    | "group-life-gradual-reduction" // Gruppeliv - Trapping
    | "group-life-annual-salary-above-x-g" // Gruppeliv - Årslønn over x G
    | "group-life-intervals" // Gruppeliv - Intervaller
    | "group-life-hta" // Gruppeliv - HTA
    | "dependents-allowance-base-coverage" // Gruppeliv - Forsørgertillegg - G
    | "dependents-allowance-annual-salary" // Gruppeliv - Forsørgertillegg - Årslønn
    | "health-insurance" // Helse
    | "travel-insurance" // Reise	
    | "disability-capital"; // Uførekapital


export interface Insurance {
	id: string; 
	provider: string; // Forsikrings leverandør (Gjensidige, IF, etc.)
	type: InsuranceTypes;
	params?: Parameter[];
}

/**
 * Canonical display order for insurance products. Mirrors the row order of the
 * payout matrix (see `CoverageMatrix`) so the create/edit customer form lists
 * products in the same predictable sequence regardless of the order the backend
 * catalog or a saved customer tree happens to return them in.
 *
 * Types not present in this list are sorted last (keeping their original
 * relative order).
 */
export const INSURANCE_DISPLAY_ORDER: InsuranceTypes[] = [
	"wc",
	"extended-wc-accident",
	"extended-wc-illness",
	"extended-wc-death",
	"leisure-accident-invalidity",
	"leisure-accident-disability",
	"leisure-accident-death",
	"trygghetsforsikring",
	"collective-accident-invalidity",
	"collective-accident-death",
	"disability-capital",
	"other-illness-invalidity",
	"other-illness-disability",
	"group-life-coordination",
	"group-life-base-coverage",
	"group-life-child-supplement",
	"group-life-annual-salary",
	"group-life-gradual-reduction",
	"group-life-annual-salary-above-x-g",
	"group-life-intervals",
	"group-life-hta",
	"dependents-allowance-base-coverage",
	"dependents-allowance-annual-salary",
	"health-insurance",
	"travel-insurance",
];

/**
 * Rank of an insurance type within {@link INSURANCE_DISPLAY_ORDER}. Unknown
 * types are pushed to the end. Use as a comparator key for a stable sort so
 * duplicate rows of the same type keep their relative order.
 */
export const insuranceDisplayRank = (type: string): number => {
	const index = INSURANCE_DISPLAY_ORDER.indexOf(type as InsuranceTypes);
	return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export interface CreateInsurance {
	type: InsuranceTypes;
	params: Parameter[];
	groups: string[];
	provider: string;
	created_at: string;
}

export interface UpdateInsurance {
	id: string;
    type: InsuranceTypes;
	params: Parameter[];
	groups: string[];
	provider: string;
}

export interface InsuranceWithGroup {
	id: string;
	type: InsuranceTypes;
	params: Parameter[];
	groups: string[];
	provider: string;
	created_at: string;
	/** Last change time for this insurance row (API / DB). */
	modified_at?: string | null;
	/** Auth0-style user payload from DB JSONB; empty object/null when unset. */
	modified_by?: unknown;
}

//do not touch this "formdata" interface
export interface CreateInsuranceFormData {
	id: string;
	type: InsuranceTypes;
	provider: string;
	mode: "create" | "update";
	for?: "invalidity" | "disability" | "death" | "accident" | "illness";
	parameters?: CreateParameterFormData[];
	groups: string[];
	error: string | null;
	isRemovable: boolean;
	/** Set when loading existing tree (update flow); used for modification history UI */
	modified_at?: string | null;
	modified_by?: unknown;
}
